import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Copy, Check, Download, Mail, CheckCircle2, XCircle, ArrowRight, ArrowLeft,
  Globe, Terminal, Shield, ExternalLink, RefreshCw, FileText,
  Grid, Type, Image as ImageIcon, Maximize2, Sliders, Volume2, Lock,
  Eye, BookOpen, Info, Compass, ChevronDown, CheckSquare, AlertTriangle,
  Play, HelpCircle
} from 'lucide-react';
import { Language } from '@/types';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';

interface BrandPageProps {
  lang: Language;
}

interface ImagePlaceholderProps {
  labelEn: string;
  labelAr: string;
  aspectRatio?: string;
  filename: string;
  isAr: boolean;
}

const ImagePlaceholder: React.FC<ImagePlaceholderProps> = ({
  labelEn,
  labelAr,
  aspectRatio = "aspect-video",
  filename,
  isAr
}) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = `/images/brand/${filename}`;

  return (
    <div className={`w-full ${aspectRatio} rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all overflow-hidden relative group`}>
      {!imageError ? (
        <div className="w-full h-full bg-white flex items-center justify-center relative select-none">
          {/* Subtle grid pattern background */}
          <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.03)_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

          <img
            src={imageSrc}
            alt={labelEn}
            onError={() => setImageError(true)}
            className="w-full h-full object-contain pointer-events-none transition-transform duration-500 group-hover:scale-[1.02] p-4 rounded-xl"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          {/* Transparent protection shield overlay */}
          <div
            className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 relative">
          <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.015)_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-white/[0.02] pointer-events-none" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/[0.02] pointer-events-none" />

          <div className="z-10 text-center space-y-3">
            <div className="w-10 h-10 rounded-lg bg-[#171C21] border border-[#262C31] flex items-center justify-center mx-auto text-[#66707A] group-hover:text-[#0E93A3] group-hover:bg-[#0E93A3]/5 transition-all">
              <ImageIcon size={18} />
            </div>
            <div>
              <h5 className="text-xs font-bold text-slate-400 group-hover:text-white transition-colors">
                {isAr ? labelAr : labelEn}
              </h5>
              <span className="text-[9px] font-mono text-slate-600 block mt-1">
                {filename}
              </span>
            </div>
          </div>

          <div className="absolute bottom-3 right-3 left-3 flex justify-between items-center text-[9px] font-mono text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <span>VEXL BRAND KIT V1.0</span>
            <span>PLACEHOLDER_FRAME</span>
          </div>
        </div>
      )}
    </div>
  );
};

interface DontsItem {
  id: string;
  textEn: string;
  textAr: string;
  filename: string;
}

const DONTS_ITEMS: DontsItem[] = [
  {
    id: 'stretch',
    textEn: "stretch or alter wordmark in any way",
    textAr: "تمطيط أو تعديل الشعار اللفظي بأي شكل من الأشكال",
    filename: "dont1.webp"
  },
  {
    id: 'blossom',
    textEn: "use the Blossom as the primary branding",
    textAr: "استخدام رمز البلوسوم كعلامة تجارية أساسية بدلاً من الشعار اللفظي",
    filename: "dont2.webp"
  },
  {
    id: 'crop',
    textEn: "crop wordmarks",
    textAr: "قص أو بتر أطراف الشعار اللفظي",
    filename: "dont3.webp"
  },
  {
    id: 'mask',
    textEn: "use wordmarks as masks",
    textAr: "استخدام الشعار اللفظي كقناع للصور (Mask)",
    filename: "dont4.webp"
  },
  {
    id: 'unapproved',
    textEn: "use any unapproved variations",
    textAr: "استخدام أي تباينات أو ألوان غير معتمدة للشعار اللفظي",
    filename: "dont5.webp"
  },
  {
    id: 'effects',
    textEn: "use any effects or textures",
    textAr: "إضافة أي تأثيرات، ظلال أو خامات على الشعار اللفظي",
    filename: "dont6.webp"
  }
];

const DosDontsCard: React.FC<{
  item: DontsItem;
  isAr: boolean;
}> = ({ item, isAr }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = `/images/brand/${item.filename}`;

  return (
    <div className="flex flex-col space-y-3 group">
      {/* Visual Box */}
      <div className="relative aspect-video rounded-xl bg-[#12161A] border border-[#262C31] hover:border-red-500/50 transition-all overflow-hidden flex items-center justify-center select-none">

        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.03)_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

        {!imageError ? (
          <img
            src={imageSrc}
            alt={item.textEn}
            onError={() => setImageError(true)}
            className="w-full h-full object-contain pointer-events-none transition-transform duration-500 group-hover:scale-[1.03] p-2 rounded-xl"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          /* Placeholder state */
          <div className="text-center space-y-2 p-4 z-10">
            <div className="w-10 h-10 rounded-lg bg-[#171C21] border border-[#262C31] flex items-center justify-center mx-auto text-[#66707A] group-hover:text-red-400 group-hover:bg-red-500/5 transition-all">
              <ImageIcon size={18} />
            </div>
            <div>
              <span className="text-[9px] font-mono text-slate-500 block uppercase tracking-wider">
                {item.filename}
              </span>
              <span className="text-[8px] font-mono text-slate-650 block mt-0.5">
                PLACEHOLDER FRAME
              </span>
            </div>
          </div>
        )}

        {/* Copy/Drag Protection Overlay */}
        <div
          className="absolute inset-0 z-20 bg-transparent select-none cursor-default"
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Lock Badge on hover */}
        <div className="absolute top-3 right-3 text-slate-400 z-30 flex items-center gap-1.5 text-[8px] font-mono tracking-widest bg-slate-950/90 px-2.5 py-1 rounded border border-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Lock size={9} className="text-red-400 animate-pulse" />
          <span>SECURE</span>
        </div>

        {/* Red X Circle in Bottom Right */}
        <div className="absolute bottom-4 right-4 z-30 w-11 h-11 rounded-full bg-red-600 flex items-center justify-center shadow-[0_4px_16px_rgba(220,38,38,0.5)] border border-red-500/40 select-none pointer-events-none transition-transform duration-300 group-hover:scale-110">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
      </div>

      {/* Caption under the box */}
      <p className="text-[11px] leading-relaxed font-sans text-slate-350 px-1">
        <span className="font-bold text-red-500 tracking-wider mr-1">{isAr ? 'ممنوع' : "DON'T"}</span>{' '}
        {isAr ? item.textAr : item.textEn}
      </p>
    </div>
  );
};

interface BlossomDosDontsItem {
  id: string;
  type: 'do' | 'dont';
  textEn: string;
  textAr: string;
  filename: string;
}

const BLOSSOM_DOS_DONTS_ITEMS: BlossomDosDontsItem[] = [
  {
    id: 'color',
    type: 'dont',
    textEn: "add any colors to the Blossom",
    textAr: "إضافة أي ألوان لرمز بلوسوم",
    filename: "dont1blo.webp"
  },
  {
    id: 'primary',
    type: 'dont',
    textEn: "use the Blossom as the primary branding",
    textAr: "استخدام رمز البلوسوم كعلامة تجارية أساسية بدلاً من الشعار اللفظي",
    filename: "dont2blo.webp"
  },
  {
    id: 'elements',
    type: 'dont',
    textEn: "add any unauthorized elements",
    textAr: "إضافة أي عناصر غير مصرح بها",
    filename: "dont3blo.webp"
  },
  {
    id: 'busy',
    type: 'dont',
    textEn: "use the Blossom over a busy image",
    textAr: "استخدام رمز بلوسوم فوق صورة مزدحمة بالتفاصيل",
    filename: "dont4blo.webp"
  },
  {
    id: 'spacing',
    type: 'do',
    textEn: "use the established spacing rules",
    textAr: "الالتزام بقواعد مسافات التباعد المقررة",
    filename: "do1blo.webp"
  },
  {
    id: 'open',
    type: 'do',
    textEn: "use the Blossom with lots of open space",
    textAr: "استخدام رمز بلوسوم مع توفير مساحات فارغة كافية حوله",
    filename: "do2blo.webp"
  }
];

const BlossomDosDontsCard: React.FC<{
  item: BlossomDosDontsItem;
  isAr: boolean;
}> = ({ item, isAr }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = `/images/brand/${item.filename}`;
  const isDo = item.type === 'do';

  return (
    <div className="flex flex-col space-y-3 group">
      {/* Visual Box */}
      <div className="relative aspect-video rounded-xl bg-white border border-slate-200/50 overflow-hidden flex items-center justify-center select-none shadow-[0_12px_40px_rgba(0,0,0,0.5)]">

        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.03)_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

        {!imageError ? (
          <img
            src={imageSrc}
            alt={item.textEn}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover pointer-events-none rounded-xl"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          /* Placeholder state */
          <div className="text-center space-y-2 p-4 z-10">
            <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto text-slate-500">
              <ImageIcon size={18} />
            </div>
            <div>
              <span className="text-[9px] font-mono text-slate-500 block uppercase tracking-wider">
                {item.filename}
              </span>
              <span className="text-[8px] font-mono text-slate-600 block mt-0.5">
                PLACEHOLDER FRAME
              </span>
            </div>
          </div>
        )}

        {/* Copy/Drag Protection Overlay */}
        <div
          className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Badge in Bottom Right */}
        {isDo ? (
          /* Green Check Circle */
          <div className="absolute bottom-4 right-4 z-30 w-11 h-11 rounded-full bg-emerald-600 flex items-center justify-center shadow-[0_4px_16px_rgba(16,185,129,0.5)] border border-emerald-500/40 select-none pointer-events-none">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        ) : (
          /* Red X Circle */
          <div className="absolute bottom-4 right-4 z-30 w-11 h-11 rounded-full bg-red-600 flex items-center justify-center shadow-[0_4px_16px_rgba(220,38,38,0.5)] border border-red-500/40 select-none pointer-events-none">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
        )}
      </div>

      {/* Caption under the box */}
      <p className="text-[11px] leading-relaxed font-sans text-slate-350 px-1">
        <span className={`font-bold tracking-wider mr-1 ${isDo ? 'text-emerald-500' : 'text-red-500'}`}>
          {isDo ? (isAr ? 'مسموح' : 'DO') : (isAr ? 'ممنوع' : "DON'T")}
        </span>{' '}
        {isAr ? item.textAr : item.textEn}
      </p>
    </div>
  );
};

interface PartnerDosDontsItem {
  id: string;
  type: 'do' | 'dont';
  textEn: string;
  textAr: string;
  filename: string;
}

const PARTNER_DOS_DONTS_ITEMS: PartnerDosDontsItem[] = [
  {
    id: 'color',
    type: 'dont',
    textEn: "add any colors to the partnership lockup",
    textAr: "إضافة أي ألوان لشعار الشراكة المشترك",
    filename: "dont1part.webp"
  },
  {
    id: 'sizes',
    type: 'dont',
    textEn: "use the wordmark at very different sizes",
    textAr: "استخدام الشعار اللفظي بأحجام متفاوتة بشكل كبير",
    filename: "dont2part.webp"
  },
  {
    id: 'blossom',
    type: 'dont',
    textEn: "use the blossom with the partnership lockup",
    textAr: "استخدام رمز بلوسوم بمفرده مع شعار الشراكة",
    filename: "dont3part.webp"
  },
  {
    id: 'busy',
    type: 'dont',
    textEn: "use the partnership lockup over a busy image",
    textAr: "وضع شعار الشراكة فوق خلفيات مزدحمة بالتفاصيل",
    filename: "dont4part.webp"
  },
  {
    id: 'spacing',
    type: 'do',
    textEn: "use the established spacing rules",
    textAr: "الالتزام بقواعد مسافات التباعد المقررة",
    filename: "do1part.webp"
  },
  {
    id: 'height',
    type: 'do',
    textEn: "strive to have the wordmarks be the same height",
    textAr: "الحرص على أن تكون الشعارات اللفظية بنفس الارتفاع البصري",
    filename: "do2part.webp"
  }
];

const PartnerDosDontsCard: React.FC<{
  item: PartnerDosDontsItem;
  isAr: boolean;
}> = ({ item, isAr }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = `/images/brand/${item.filename}`;
  const isDo = item.type === 'do';

  return (
    <div className="flex flex-col space-y-3 group">
      {/* Visual Box */}
      <div className="relative aspect-video rounded-xl bg-white border border-slate-200/50 overflow-hidden flex items-center justify-center select-none shadow-[0_12px_40px_rgba(0,0,0,0.5)]">

        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.03)_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

        {!imageError ? (
          <img
            src={imageSrc}
            alt={item.textEn}
            onError={() => setImageError(true)}
            className="w-full h-full object-contain pointer-events-none rounded-xl p-2"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          /* Placeholder state */
          <div className="text-center space-y-2 p-4 z-10">
            <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto text-slate-500">
              <ImageIcon size={18} />
            </div>
            <div>
              <span className="text-[9px] font-mono text-slate-500 block uppercase tracking-wider">
                {item.filename}
              </span>
              <span className="text-[8px] font-mono text-slate-650 block mt-0.5">
                PLACEHOLDER FRAME
              </span>
            </div>
          </div>
        )}

        {/* Copy/Drag Protection Overlay */}
        <div
          className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Badge in Bottom Right */}
        {isDo ? (
          /* Green Check Circle */
          <div className="absolute bottom-4 right-4 z-30 w-11 h-11 rounded-full bg-emerald-600 flex items-center justify-center shadow-[0_4px_16px_rgba(16,185,129,0.5)] border border-emerald-500/40 select-none pointer-events-none">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        ) : (
          /* Red X Circle */
          <div className="absolute bottom-4 right-4 z-30 w-11 h-11 rounded-full bg-red-600 flex items-center justify-center shadow-[0_4px_16px_rgba(220,38,38,0.5)] border border-red-500/40 select-none pointer-events-none">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
        )}
      </div>

      {/* Caption under the box */}
      <p className="text-[11px] leading-relaxed font-sans text-slate-350 px-1">
        <span className={`font-bold tracking-wider mr-1 ${isDo ? 'text-emerald-500' : 'text-red-500'}`}>
          {isDo ? (isAr ? 'مسموح' : 'DO') : (isAr ? 'ممنوع' : "DON'T")}
        </span>{' '}
        {isAr ? item.textAr : item.textEn}
      </p>
    </div>
  );
};

interface ColorToken {
  nameEn: string;
  nameAr: string;
  hex: string;
  rgb: string;
  hsl: string;
  variable: string;
  typeEn: string;
  typeAr: string;
  bgClass: string;
  textClass: string;
}

const COLOR_TOKENS: ColorToken[] = [
  {
    nameEn: 'Void',
    nameAr: 'الفراغ الكوني',
    hex: '#05070A',
    rgb: '5, 7, 10',
    hsl: '216°, 33%, 3%',
    variable: '--color-void',
    typeEn: 'Page Background',
    typeAr: 'خلفية الصفحة الأساسية — أقرب لصورة ليلية فضائية',
    bgClass: 'bg-[#05070A]',
    textClass: 'text-white border border-white/10'
  },
  {
    nameEn: 'Void Soft',
    nameAr: 'الفراغ الناعم',
    hex: '#0B0F12',
    rgb: '11, 15, 18',
    hsl: '206°, 24%, 6%',
    variable: '--color-void-soft',
    typeEn: 'Section Band Separator',
    typeAr: 'فاصل الأقسام الفرعية',
    bgClass: 'bg-[#0B0F12]',
    textClass: 'text-white border border-white/10'
  },
  {
    nameEn: 'Surface Panel',
    nameAr: 'سطح اللوحة',
    hex: '#12161A',
    rgb: '18, 22, 26',
    hsl: '210°, 18%, 9%',
    variable: '--color-surface-panel',
    typeEn: 'Feature Card Background',
    typeAr: 'خلفية كروت المميزات',
    bgClass: 'bg-[#12161A]',
    textClass: 'text-white border border-white/10'
  },
  {
    nameEn: 'Surface Card',
    nameAr: 'سطح الكارت',
    hex: '#171C21',
    rgb: '23, 28, 33',
    hsl: '210°, 18%, 11%',
    variable: '--color-surface-card',
    typeEn: 'Data/Chart Card Background',
    typeAr: 'خلفية كروت البيانات والرسوم البيانية',
    bgClass: 'bg-[#171C21]',
    textClass: 'text-white border border-white/10'
  },
  {
    nameEn: 'Signal Teal',
    nameAr: 'تيل الإشارة',
    hex: '#0E93A3',
    rgb: '14, 147, 163',
    hsl: '186°, 84%, 35%',
    variable: '--color-signal-teal',
    typeEn: 'Primary Action — Buttons, Links, Brand Mark',
    typeAr: 'اللون الأساسي للأزرار والروابط وعلامة البراند',
    bgClass: 'bg-[#0E93A3]',
    textClass: 'text-white'
  },
  {
    nameEn: 'Signal Teal Deep',
    nameAr: 'تيل الإشارة العميق',
    hex: '#075C68',
    rgb: '7, 92, 104',
    hsl: '187°, 87%, 22%',
    variable: '--color-signal-teal-deep',
    typeEn: 'Hover/Pressed State for Primary Actions',
    typeAr: 'حالة التمرير والضغط للأزرار الأساسية',
    bgClass: 'bg-[#075C68]',
    textClass: 'text-white'
  },
  {
    nameEn: 'NDVI 500 (Vegetation)',
    nameAr: 'مؤشر NDVI 500 (الغطاء النباتي)',
    hex: '#639922',
    rgb: '99, 153, 34',
    hsl: '87°, 64%, 37%',
    variable: '--color-ndvi-500',
    typeEn: 'Healthy Vegetation Status — Data Only, Not Buttons',
    typeAr: 'حالة الغطاء النباتي الصحي — للبيانات فقط وليس للأزرار',
    bgClass: 'bg-[#639922]',
    textClass: 'text-white'
  },
  {
    nameEn: 'Water 500 (Hydrology)',
    nameAr: 'مؤشر المياه 500 (الهيدرولوجيا)',
    hex: '#185F82',
    rgb: '24, 95, 130',
    hsl: '200°, 69%, 30%',
    variable: '--color-water-500',
    typeEn: 'Moisture Index Charts, Water Overlays',
    typeAr: 'رسوم مؤشرات الرطوبة وطبقات المياه',
    bgClass: 'bg-[#185F82]',
    textClass: 'text-white'
  },
  {
    nameEn: 'Thermal 400 (Drought)',
    nameAr: 'مؤشر حراري 400 (الجفاف)',
    hex: '#BA7517',
    rgb: '186, 117, 23',
    hsl: '35°, 78%, 41%',
    variable: '--color-thermal-400',
    typeEn: 'Drought Severity, Heat Stress Overlays',
    typeAr: 'مؤشرات شدة الجفاف وطبقات الإجهاد الحراري',
    bgClass: 'bg-[#BA7517]',
    textClass: 'text-white'
  },
  {
    nameEn: 'Risk 500 (Alert)',
    nameAr: 'مؤشر المخاطر 500 (التنبيهات)',
    hex: '#993C1D',
    rgb: '153, 60, 29',
    hsl: '15°, 68%, 36%',
    variable: '--color-risk-500',
    typeEn: 'Risk/Alert States Only — Not Decorative',
    typeAr: 'حالات المخاطر والتنبيهات فقط — ليس للزينة',
    bgClass: 'bg-[#993C1D]',
    textClass: 'text-white'
  },
  {
    nameEn: 'Hairline',
    nameAr: 'خط فاصل رفيع',
    hex: '#262C31',
    rgb: '38, 44, 49',
    hsl: '207°, 13%, 17%',
    variable: '--color-hairline',
    typeEn: 'Default 1px Borders',
    typeAr: 'الحدود الافتراضية بسمك 1 بكسل',
    bgClass: 'bg-[#262C31]',
    textClass: 'text-white border border-white/10'
  },
  {
    nameEn: 'Ink Primary',
    nameAr: 'حبر أساسي',
    hex: '#EDF0F1',
    rgb: '237, 240, 241',
    hsl: '195°, 11%, 94%',
    variable: '--color-ink-primary',
    typeEn: 'Body Text on Dark Backgrounds',
    typeAr: 'نص الجسم على الخلفيات الداكنة',
    bgClass: 'bg-[#EDF0F1]',
    textClass: 'text-black'
  },
  {
    nameEn: 'Ink Secondary',
    nameAr: 'حبر ثانوي',
    hex: '#9AA5AC',
    rgb: '154, 165, 172',
    hsl: '203°, 10%, 64%',
    variable: '--color-ink-secondary',
    typeEn: 'Supporting Text',
    typeAr: 'النصوص الداعمة والثانوية',
    bgClass: 'bg-[#9AA5AC]',
    textClass: 'text-black'
  }
];

const VectorPixelLoop: React.FC<{ isAr: boolean }> = ({ isAr }) => {
  const [step, setStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);

  useEffect(() => {
    if (!isPlaying) return;
    const interval = 3000; // 3 seconds
    const stepsCount = 3;
    const startTime = Date.now() - (progressRef.current / 100) * interval;

    let animationFrameId: number;

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const currentProgress = Math.min((elapsed / interval) * 100, 100);
      progressRef.current = currentProgress;

      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${currentProgress}%`;
      }

      if (currentProgress >= 100) {
        progressRef.current = 0;
        if (progressBarRef.current) {
          progressBarRef.current.style.width = '0%';
        }
        setStep((prev) => (prev + 1) % stepsCount);
      } else {
        animationFrameId = requestAnimationFrame(tick);
      }
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPlaying, step]);

  const steps = [
    {
      id: 'vector',
      title: 'Vector',
      titleAr: 'المتجهات (Vector)',
      badgeEn: '01 / GEOMETRIC PATHS',
      badgeAr: '٠١ / المسارات الهندسية',
      descEn: "Representing the left side of our trademark 'V'. Precision-defined mathematical paths and anchor nodes forming perfect geometric slopes.",
      descAr: "يمثل الشق الأيسر من علامة الـ 'V' الخاصة بنا. مسارات رياضية ونقاط إرساء محددة بدقة متناهية لتشكيل خطوط هندسية مثالية.",
      color: '#0E93A3',
      glow: 'rgba(14, 147, 163, 0.08)',
    },
    {
      id: 'pixel',
      title: 'Pixel',
      titleAr: 'البكسل (Pixel)',
      badgeEn: '02 / RASTER GRID ARRAY',
      badgeAr: '٠٢ / مصفوفة البكسل النقطية',
      descEn: "Representing the right side of our trademark 'V'. A structured raster grid showing individual physical pixels mapped onto coordinate matrices.",
      descAr: "يمثل الشق الأيمن من علامة الـ 'V' الخاصة بنا. مصفوفة بكسل نقطية منظمة تُظهر المربعات المادية الموزعة على شبكة الإحداثيات.",
      color: '#185F82',
      glow: 'rgba(24, 95, 130, 0.08)',
    },
    {
      id: 'vexl',
      title: 'VEXL',
      titleAr: 'فكسل (VEXL)',
      badgeEn: '03 / SYNTHETIC INTEGRATION',
      badgeAr: '٠٣ / الاندماج المتكامل',
      descEn: "The ultimate synthesis. Vector precision on the left seamlessly merging with structural pixel elements on the right under the unified VEXL brand.",
      descAr: "الاندماج النهائي الأمثل. دقة المتجهات في الشق الأيسر تندمج بسلاسة مع البكسلات البنائية في الشق الأيمن تحت هوية فكسل الموحدة.",
      color: '#0E93A3',
      glow: 'rgba(14, 147, 163, 0.12)',
    }
  ];

  return (
    <div
      className="w-full bg-[#12161A] border border-[#262C31] rounded-2xl p-6 md:p-8 relative min-h-[400px] flex flex-col lg:flex-row gap-8 items-center lg:items-stretch justify-between overflow-hidden transition-all duration-300"
    >
      {/* Dynamic Glow Background */}
      <motion.div
        animate={{
          background: `radial-gradient(circle at 80% 50%, ${steps[step].glow} 0%, transparent 60%)`
        }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 pointer-events-none z-0"
      />

      {/* Grid Pattern Background */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.01)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />

      <div className="z-10 w-full flex flex-col lg:flex-row gap-8 items-center lg:items-stretch">
        {/* Left Column: Info Panel */}
        <div className="flex flex-col justify-between w-full lg:w-3/5 space-y-6 text-left">

          {/* Section Header with Premium Formula Badge */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/5 pb-4">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                <span className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                  {isAr ? 'الاندماج الرياضي والنقطي' : 'SYNTHESIS PARADIGM'}
                </span>
              </div>
              <span className="text-[10px] font-mono text-slate-500 tracking-wider">
                VEXL = Vector + Pixel
              </span>
            </div>

            {/* Formula display */}
            <div className="flex items-center gap-2 md:gap-3 text-2xl md:text-3xl font-extrabold tracking-wide mt-2">
              <span style={{ color: '#0E93A3' }}>Vector</span>
              <span className="text-white/20 font-light">+</span>
              <span style={{ color: '#185F82' }}>Pixel</span>
              <span className="text-white/20 font-light">=</span>
              <span className="px-3 py-0.5 rounded-md text-white bg-[#171C21] border border-[#262C31]">VEXL</span>
            </div>
          </div>

          {/* Active step details with slide animation */}
          <div className="space-y-3 min-h-[130px] flex flex-col justify-center text-left">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                className="space-y-2"
              >
                <div>
                  <span
                    className="px-2.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase border"
                    style={{
                      color: steps[step].color,
                      borderColor: `${steps[step].color}30`,
                      backgroundColor: `${steps[step].color}08`
                    }}
                  >
                    {isAr ? steps[step].badgeAr : steps[step].badgeEn}
                  </span>
                </div>

                <h4 className="text-xl md:text-2xl font-bold tracking-tight text-white">
                  {isAr ? steps[step].titleAr : steps[step].title}
                </h4>

                <p className="text-slate-350 text-xs md:text-sm leading-relaxed max-w-xl">
                  {isAr ? steps[step].descAr : steps[step].descEn}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Vercel-style pill selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
            <div className="flex bg-slate-950/80 p-1 rounded-full border border-white/10 w-fit relative overflow-hidden">
              {steps.map((tab, idx) => {
                const isActive = step === idx;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      progressRef.current = 0;
                      if (progressBarRef.current) {
                        progressBarRef.current.style.width = '0%';
                      }
                      setStep(idx);
                    }}
                    className={`relative px-5 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 z-10 ${isActive ? 'text-white' : 'text-slate-400 hover:text-white/80'
                      }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activePill"
                        className="absolute inset-0 rounded-full shadow-lg z-[-1]"
                        style={{
                          backgroundColor: tab.color,
                        }}
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                    {tab.title}
                  </button>
                );
              })}
            </div>

            {/* Active progress bar indicator next to/under pill */}
            <div className="w-40 h-[3px] bg-white/[0.04] rounded-full overflow-hidden self-center hidden sm:block">
              <div
                ref={progressBarRef}
                className="h-full rounded-full"
                style={{
                  width: `${progressRef.current}%`,
                  backgroundColor: steps[step].color,
                  boxShadow: `0 0 8px ${steps[step].color}`,
                  transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
                }}
              />
            </div>
          </div>

          {/* Playback Controls & Minimal info */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5 text-[10px] font-medium text-slate-500">
            <div className="flex items-center gap-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPlaying(!isPlaying);
                }}
                className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors py-1 px-2 rounded-md hover:bg-white/5"
              >
                {isPlaying ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="text-amber-500">
                    <rect x="4" y="4" width="4" height="16" rx="1" />
                    <rect x="16" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-500">
                    <polygon points="5 3 19 12 5 21" />
                  </svg>
                )}
                <span>{isPlaying ? (isAr ? 'إيقاف مؤقت' : 'PAUSE') : (isAr ? 'تشغيل' : 'PLAY')}</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  progressRef.current = 0;
                  if (progressBarRef.current) {
                    progressBarRef.current.style.width = '0%';
                  }
                  setStep((prev) => (prev + 1) % 3);
                }}
                className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors py-1 px-2 rounded-md hover:bg-white/5"
              >
                <RefreshCw size={10} />
                <span>{isAr ? 'التالي' : 'NEXT'}</span>
              </button>
            </div>

            <div className="text-[10px] font-sans text-slate-550 uppercase tracking-wider">
              {isAr ? 'محاكي العرض الثنائي' : 'Dual-Mode Visualizer'}
            </div>
          </div>
        </div>

        {/* Right Column: Live Visualizer Grid */}
        <div className="w-full lg:w-2/5 flex flex-col items-center justify-center min-h-[300px] lg:min-h-0 relative">
          <div className="relative p-6 bg-slate-950/80 rounded-2xl border border-white/10 flex items-center justify-center select-none shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden">
            <div className="relative w-64 h-64 bg-[#020610]/40 border border-white/5 rounded-xl overflow-hidden">
              {/* Background grid lines */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] [background-size:18px_18px] [background-position:20px_20px] pointer-events-none" />

              {/* Grid Cells (12x12 high-resolution raster grid) */}
              {Array.from({ length: 144 }).map((_, idx) => {
                const row = Math.floor(idx / 12);
                const col = idx % 12;

                // Right leg (Pixel side) indices (dispersing to top-right to match real VEXL logo on a 12x12 grid)
                const rightLegIndices = [137, 125, 126, 114, 102, 103, 91, 79, 80, 68, 56, 57, 45, 33, 34, 22, 10];
                const isRightLeg = rightLegIndices.includes(idx);

                return (
                  <motion.div
                    key={idx}
                    animate={
                      step === 1 && isRightLeg
                        ? {
                          backgroundColor: 'rgba(14, 147, 163, 0.7)',
                          borderColor: '#0E93A3',
                          boxShadow: '0 0 6px rgba(14, 147, 163, 0.3)',
                        }
                        : step === 2 && isRightLeg
                          ? {
                            backgroundColor: 'rgba(14, 147, 163, 0.7)',
                            borderColor: '#0E93A3',
                            boxShadow: '0 0 8px rgba(14, 147, 163, 0.4)',
                          }
                          : {
                            backgroundColor: 'rgba(255, 255, 255, 0.005)',
                            borderColor: 'rgba(255, 255, 255, 0.02)',
                            boxShadow: 'none',
                          }
                    }
                    transition={{
                      duration: 0.25,
                      delay: step === 1 && isRightLeg ? ((11 - row) * 0.05) : 0, // staggered cascade bottom-up during pixel rasterizing
                    }}
                    className="absolute border rounded-[1.5px]"
                    style={{
                      left: `${20 + col * 18 + 1}px`, // 1px spacing offset
                      top: `${20 + row * 18 + 1}px`,
                      width: '16px',
                      height: '16px',
                    }}
                  />
                );
              })}

              {/* Vector/Synthesis SVG overlay */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible">
                <AnimatePresence>
                  {step === 0 && (
                    <g className="drop-shadow-[0_0_6px_rgba(14,147,163,0.4)]">
                      {/* Architectural Vector Polygon representation of Left Leg */}
                      <motion.path
                        key="vector-polygon"
                        d="M 38 29 L 110 227 L 128 227 L 56 29 Z"
                        fill="#0E93A3"
                        fillOpacity="0.08"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      />
                      <motion.path
                        key="vector-left-line"
                        d="M 38 29 L 110 227"
                        fill="none"
                        stroke="#0E93A3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                      <motion.path
                        key="vector-right-line"
                        d="M 56 29 L 128 227"
                        fill="none"
                        stroke="#0E93A3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                      <motion.path
                        key="vector-center-dash"
                        d="M 47 29 L 119 227"
                        fill="none"
                        stroke="#0E93A3"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeDasharray="4,3"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.0, ease: "easeInOut" }}
                      />
                    </g>
                  )}
                  {step === 2 && (
                    <g className="drop-shadow-[0_0_6px_rgba(14,147,163,0.6)]">
                      <motion.path
                        key="vexl-polygon"
                        d="M 38 29 L 110 227 L 128 227 L 56 29 Z"
                        fill="#0E93A3"
                        fillOpacity="0.08"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      />
                      <motion.path
                        key="vexl-left-line"
                        d="M 38 29 L 110 227"
                        fill="none"
                        stroke="#0E93A3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                      <motion.path
                        key="vexl-right-line"
                        d="M 56 29 L 128 227"
                        fill="none"
                        stroke="#0E93A3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                      <motion.path
                        key="vexl-center-dash"
                        d="M 47 29 L 119 227"
                        fill="none"
                        stroke="#0E93A3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray="6,4"
                        animate={{ strokeDashoffset: [0, -20] }}
                        transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
                        exit={{ opacity: 0 }}
                      />
                    </g>
                  )}
                </AnimatePresence>

                {/* Nodes rendering for Vector/VEXL mode */}
                 {(step === 0 || step === 2) && (
                  <>
                    {/* Node 1: Outer Top-Left Anchor */}
                    <g>
                      <circle cx="38" cy="29" r="4.5" fill="#FFFFFF" className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" />
                      <circle cx="38" cy="29" r="2" fill="#0E93A3" />
                    </g>
                    {/* Node 2: Inner Top-Left Anchor */}
                    <g>
                      <circle cx="56" cy="29" r="4.5" fill="#FFFFFF" className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" />
                      <circle cx="56" cy="29" r="2" fill="#0E93A3" />
                    </g>
                    {/* Node 3: Center Bottom Anchor */}
                    <g>
                      <circle cx="119" cy="227" r="4.5" fill="#FFFFFF" className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" />
                      <circle cx="119" cy="227" r="2" fill="#0E93A3" />
                    </g>
                  </>
                )}
              </svg>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export const BrandPage: React.FC<BrandPageProps> = ({ lang }) => {
  const navigate = useNavigate();
  const isAr = lang === 'ar';

  const [activeSection, setActiveSection] = useState('cover');
  const [copiedHex, setCopiedHex] = useState<string | null>(null);
  const [copiedSvg, setCopiedSvg] = useState<string | null>(null);
  const [activeFontTab, setActiveFontTab] = useState<'outfit' | 'inter' | 'mono'>('outfit');

  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setEmailSubmitted(true);
    }, 800);
  };

  const watermarkVideoRef = useRef<HTMLVideoElement>(null);

  // Keep the watermark video playing continuously and loop infinitely using a robust observer and events
  useEffect(() => {
    const video = watermarkVideoRef.current;
    if (!video) return;

    // Force muted and defaultMuted properties on the DOM element to bypass React attribute bugs and satisfy browser autoplay policies
    video.muted = true;
    video.defaultMuted = true;

    // Initial play attempt
    video.play().catch(() => { });

    // Ensure it plays on focus and visibility change
    const handlePlayAttempt = () => {
      if (document.visibilityState === 'visible' && video) {
        video.play().catch(() => { });
      }
    };

    window.addEventListener('focus', handlePlayAttempt);
    document.addEventListener('visibilitychange', handlePlayAttempt);

    // If native looping fails or halts, reset currentTime to 0 and play on ended
    const handleEnded = () => {
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => { });
      }
    };
    video.addEventListener('ended', handleEnded);

    // Setup IntersectionObserver to play when in viewport and pause when out to prevent browser throttling/freezes
    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            video.play().catch(() => { });
          } else {
            video.pause();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(video);
    }

    return () => {
      window.removeEventListener('focus', handlePlayAttempt);
      document.removeEventListener('visibilitychange', handlePlayAttempt);
      video.removeEventListener('ended', handleEnded);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Force Left-to-Right layout on the document element while on this page
  useEffect(() => {
    document.documentElement.dir = 'ltr';
    document.documentElement.setAttribute('dir', 'ltr');

    return () => {
      // Restore natural layout direction of the active language upon leaving the page
      const naturalDir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.dir = naturalDir;
      document.documentElement.setAttribute('dir', naturalDir);
    };
  }, [lang]);

  // References for all 13 sections
  const sectionRefs = {
    cover: useRef<HTMLDivElement>(null),
    introduction: useRef<HTMLDivElement>(null),
    mission: useRef<HTMLDivElement>(null),
    watermark: useRef<HTMLDivElement>(null),
    geometry: useRef<HTMLDivElement>(null),
    dos: useRef<HTMLDivElement>(null),
    blossomlogo: useRef<HTMLDivElement>(null),
    colorsystem: useRef<HTMLDivElement>(null),
    typography: useRef<HTMLDivElement>(null),
    radius: useRef<HTMLDivElement>(null),
    naming: useRef<HTMLDivElement>(null),
    lockups: useRef<HTMLDivElement>(null),
    assets: useRef<HTMLDivElement>(null)
  };

  const sections = [
    { id: 'cover', num: '00', titleEn: 'Cover', titleAr: 'الغلاف' },
    { id: 'introduction', num: '01', titleEn: 'Introduction & Trademark', titleAr: 'المقدمة والعلامات التجارية' },
    { id: 'mission', num: '02', titleEn: 'Mission & Values', titleAr: 'الرسالة والقيم' },
    { id: 'watermark', num: '03', titleEn: 'Co-Branding Watermark', titleAr: 'العلامة المائية المشتركة' },
    { id: 'geometry', num: '04', titleEn: 'Logo Geometry', titleAr: 'هندسة الشعار' },
    { id: 'dos', num: '05', titleEn: "Dos and Don'ts: Wordmark", titleAr: 'المسموح والممنوع: الشعار اللفظي' },
    { id: 'blossomlogo', num: '06', titleEn: 'Blossom Logo', titleAr: 'شعار بلوسوم' },
    { id: 'colorsystem', num: '07', titleEn: 'Color Registry', titleAr: 'سجل الألوان' },
    { id: 'typography', num: '08', titleEn: 'Typography Hierarchy', titleAr: 'تنسيق الخطوط' },
    { id: 'radius', num: '09', titleEn: 'Border Radius', titleAr: 'انحناء الحواف' },
    { id: 'naming', num: '10', titleEn: 'Product Naming', titleAr: 'سجل مسميات المنتجات' },
    { id: 'lockups', num: '11', titleEn: 'Partner Lockups', titleAr: 'قواعد دمج الشعارات' },
    { id: 'assets', num: '12', titleEn: 'Assets Center', titleAr: 'مكتبة الأصول' }
  ];

  // Intersection observer to highlight sidebar on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      for (const section of sections) {
        const ref = sectionRefs[section.id as keyof typeof sectionRefs];
        if (ref.current) {
          const offsetTop = ref.current.offsetTop;
          const offsetHeight = ref.current.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const ref = sectionRefs[id as keyof typeof sectionRefs];
    if (ref.current) {
      const yOffset = -150; // Increased spacing to push elements lower on scroll
      const y = ref.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  const handleCopyHex = (hex: string) => {
    navigator.clipboard.writeText(hex);
    setCopiedHex(hex);
    setTimeout(() => setCopiedHex(null), 2000);
  };

  const handleCopySvg = (id: string, code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedSvg(id);
    setTimeout(() => setCopiedSvg(null), 2000);
  };

  const VEXL_LOGO_SVG = `<svg width="120" height="30" viewBox="0 0 120 30" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M5 5L15 25L25 5" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M30 5H45V10H35V15H42V20H35V25H45" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M50 5L62 25M62 5L50 25" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M68 5H78V25" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;



  const VEXL_SYMBOL_SVG = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M8 8L20 32L32 8" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

  return (
    <PageTransition>
      <div
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-24 pb-20 relative ${isAr ? 'font-arabic' : 'font-sans'}`}
        dir="ltr"
      >
        <SEO
          title={isAr ? 'إرشادات التصميم المتكاملة | VEXL' : 'Complete Design Guidelines | VEXL'}
          description={isAr ? 'الدليل الرسمي الفني وإرشادات التصميم لشركة فكسل.' : 'Official design guidelines, design system, and assets registry for VEXL.'}
        />

        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(14,147,163,0.03)_0%,transparent_75%)]" />
        </div>

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16 relative z-10 w-full">

          {/* Back button */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-xs font-mono tracking-widest uppercase"
            >
              <ArrowLeft size={14} />
              <span>{isAr ? 'العودة للخلف' : 'Back'}</span>
            </button>
          </div>

          {/* Main Content Container - Full Width */}
          <div className="w-full">
            <main className="space-y-32 w-full">

              {/* 01 Cover Page (Hero — Two-Column Editorial Split) */}
              <div
                ref={sectionRefs.cover}
                id="cover"
                className="scroll-mt-28 min-h-[42vh] flex items-center pb-16 border-b border-[#262C31]"
              >
                <div
                  className={`w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start ${
                    isAr ? 'text-right' : 'text-left'
                  }`}
                >
                  {/* Left Column (lg:col-span-7) */}
                  <div className="lg:col-span-7 space-y-5">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0E93A3]/10 border border-[#0E93A3]/20 text-[#0E93A3] text-[10px] font-mono uppercase tracking-wider">
                      <Shield size={12} />
                      <span>
                        {isAr
                          ? 'الوثيقة الرسمية المعتمدة // إصدار الهوية المؤسسية'
                          : 'Official Enterprise Release // Identity v2.0'}
                      </span>
                    </div>

                    <h1
                      className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                      style={{
                        fontFamily:
                          "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif",
                      }}
                    >
                      {isAr
                        ? 'إرشادات الشعار والهوية البصرية لـ VEXL'
                        : 'VEXL Logo and Brand Guidelines'}
                    </h1>

                    <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono text-slate-500 pt-1">
                      <span className="text-[#0E93A3] font-semibold">// SPEC-2026.04</span>
                      <span>•</span>
                      <span>
                        {isAr
                          ? 'معيار التصميم الجيومكاني الموحد'
                          : 'Unified Geospatial Design Standard'}
                      </span>
                      <span>•</span>
                      <span className="text-slate-400">EGIPA #5007</span>
                    </div>
                  </div>

                  {/* Right Column (lg:col-span-5) */}
                  <div className="lg:col-span-5 space-y-6">
                    <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                      {isAr
                        ? 'إن شعار فكسل والعناصر الأخرى للهوية البصرية هي ركائز أساسية لهويتنا الفريدة وتميز شركتنا في بناء وتطوير أنظمة الذكاء المكاني والسيادية.'
                        : 'VEXL’s logo, geometric mark, and branded visual systems are foundational to our unique identity and technical differentiation.'}
                    </p>

                    <div className="p-4 rounded-xl bg-[#12161A] border border-[#262C31] text-xs leading-relaxed text-slate-300 space-y-1">
                      <span className="font-bold text-[#0E93A3] block font-mono text-[10px] uppercase tracking-wider">
                        {isAr ? 'تنبيه الملكية الفكرية والترخيص' : 'LEGAL & TRADEMARK NOTICE'}
                      </span>
                      <p>
                        {isAr
                          ? 'لا يجوز استخدام هذه الأصول بأي شكل من الأشكال ما لم يُصرح بذلك صراحةً وكتابةً من قِبل VEXL.'
                          : 'These assets may not be used in any manner that isn’t expressly authorized in writing by VEXL.'}
                      </p>
                    </div>

                    <div className={`flex flex-wrap gap-3 pt-1 ${isAr ? 'justify-end' : 'justify-start'}`}>
                      <button
                        type="button"
                        onClick={() => scrollToSection('assets')}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-mono uppercase tracking-wider font-medium bg-[#0E93A3] text-white hover:bg-[#075C68] shadow-[0_0_15px_rgba(14,147,163,0.3)] transition-all cursor-pointer"
                      >
                        <Download size={13} />
                        <span>{isAr ? 'تحميل كيت الهوية' : 'Download Brand Kit'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollToSection('geometry')}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-mono uppercase tracking-wider font-medium bg-[#12161A] text-[#EDF0F1] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all cursor-pointer"
                      >
                        <span>{isAr ? 'هندسة الشعار' : 'Logo Geometry'}</span>
                        <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 02 Introduction & Trademark */}
              <div ref={sectionRefs.introduction} id="introduction" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">01 INTRODUCTION & TRADEMARK</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'المقدمة والعلامات التجارية لـ VEXL' : 'Introduction & Trademark Guidelines'}
                  </h3>
                </div>

                <div className="p-6 rounded-2xl bg-[#12161A] border border-[#262C31] relative overflow-hidden space-y-4">
                  <p className="text-[#9AA5AC] text-xs md:text-sm leading-relaxed font-normal">
                    {isAr ? (
                      <>
                        إن اسم <strong>&quot;VEXL&quot;</strong> وشعار VEXL وغيرها من العلامات التجارية لـ VEXL هي ملكية حصرية لشركة VEXL. تهدف هذه الإرشادات إلى مساعدة شركائنا وموزعينا وعملائنا ومطورينا ومستشارينا والناشرين وأي أطراف ثالثة أخرى على فهم كيفية استخدام وعرض علاماتنا التجارية وأعمالنا المحمية بحقوق الطبع والنشر في أصولهم الرقمية والمطبوعة.
                      </>
                    ) : (
                      <>
                        The <strong>&quot;VEXL&quot;</strong> name, the VEXL logo, and other VEXL trademarks are property of VEXL. These guidelines are intended to help our partners, resellers, customers, developers, consultants, publishers, and any other third parties understand how to use and display our trademarks and copyrighted work in their own assets and materials.
                      </>
                    )}
                  </p>
                </div>

                {/* Looping vector + pixel component */}
                <VectorPixelLoop isAr={isAr} />
              </div>

              {/* 03 Mission & Values */}
              <div ref={sectionRefs.mission} id="mission" className="scroll-mt-28 space-y-6 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">02 MISSION & VALUES</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'الرسالة والقيم الجوهرية' : 'The Core Pillars'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'تقوم فلسفة فكسل على دمج دقة النمذجة الرياضية والخرائطية مع بساطة واجهات الذكاء الاصطناعي الجغرافي. نتحرك بثبات نحو تمكين المؤسسات الكبرى من فهم البيانات المكانية دون تعقيد.'
                    : 'The visual logic of VEXL balances mathematical precision with functional clarity. We design to empower enterprise users, ensuring spatial complex grids translate into clean, actionable, high-performance interfaces.'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                  <div className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] space-y-3">
                    <div className="text-xs font-mono text-[#0E93A3]">01 / {isAr ? 'البساطة الجريئة' : 'Stark Contrast'}</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed font-normal">
                      {isAr ? 'مستويات فراغ واسعة، خطوط متباينة بوضوح، وخلفيات داكنة عميقة.' : 'Ample white space, high typographic hierarchy, and solid deep dark bases.'}
                    </p>
                  </div>
                  <div className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] space-y-3">
                    <div className="text-xs font-mono text-[#0E93A3]">02 / {isAr ? 'الدقة المكانية' : 'Spatial Precision'}</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed font-normal">
                      {isAr ? 'عناصر واجهات هندسية تعكس دقة المتجهات والبيانات الجيومكانية.' : 'Grid systems and vector details aligning to precision coordinates.'}
                    </p>
                  </div>
                  <div className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] space-y-3">
                    <div className="text-xs font-mono text-[#D4A24C]">03 / {isAr ? 'منظومة متكاملة' : 'Unified Suite'}</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed font-normal">
                      {isAr ? 'تطابق بصري تام يربط بين خدمات المنصة السحابية وتطبيق الديسكتوب.' : 'Seamless visual correlation linking cloud engines with desktop runtimes.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* 04 Co-Branding Watermark */}
              <div ref={sectionRefs.watermark} id="watermark" className="scroll-mt-28 space-y-6 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">03 CO-BRANDING WATERMARK</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'العلامة المائية وشعارات التوثيق المشترك' : 'Co-Branding Watermark System'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'يتم تطبيق العلامة المائية المتحركة لتوثيق المصادر وتأكيد موثوقية الخرائط الجغرافية والتقارير الفنية. تُثبت في زاوية الشاشة أو كخلفية لتوثيق ملكية البيانات.'
                    : 'The motion watermark establishes official digital signatures on dynamic map animations, video outputs, and telemetry screens. It loops continuously as a background context cue.'}
                </p>

                <div className="w-full max-w-5xl mx-auto rounded-2xl border border-[#262C31] bg-[#12161A] p-4 relative overflow-hidden group">
                  {/* Subtle scanline effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.01] to-transparent pointer-events-none z-10 [background-size:100%_4px]" />
                  <div className="absolute top-3 left-4 flex gap-1.5 z-10">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/40 border border-red-500/20" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/40 border border-yellow-500/20" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/40 border border-green-500/20" />
                  </div>
                  <div className="absolute top-2.5 right-4 text-[8px] font-mono text-slate-500 uppercase tracking-widest z-10">
                    DYNAMIC WATERMARK LAYER
                  </div>

                  <div className="mt-6 rounded-xl overflow-hidden border border-white/5 bg-black relative aspect-video select-none">
                    <video
                      ref={watermarkVideoRef}
                      src="/images/brand/watermark.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controlsList="nodownload"
                      className="w-full h-full object-cover pointer-events-none"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    {/* Transparent protection shield overlay */}
                    <div
                      className="absolute inset-0 z-20 bg-transparent select-none cursor-default"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between text-[9px] font-mono text-slate-500">
                    <span>STATUS: LOCKUP_ACTIVE</span>
                    <span>1080P PRORES / H.264</span>
                  </div>
                </div>
              </div>

              {/* 05 Logo Grid & Geometry */}
              <div ref={sectionRefs.geometry} id="geometry" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">04 LOGO GRID & GEOMETRY</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'الهندسة الرياضية لشعار فكسل' : 'Wordmark Construction Geometry'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'يتكون بناء شعار فكسل من إطارين مرجعيين رسميين: الشعار اللفظي الصافي (يسار) ومخطط مساحة الأمان الهندسية (يمين) الذي يحدد الحد الأدنى لهوامش العزل حول الشعار.'
                    : 'The core VEXL wordmark construction consists of two official reference frames: the balanced standalone wordmark (left) and the structural clear space bounds (right) that establish the minimum isolation padding around the logo.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  {/* Left Frame: VEXL Standalone Wordmark */}
                  <div className="relative overflow-hidden rounded-2xl border border-[#262C31] bg-[#12161A] p-4 group w-full">
                    {/* Lock Badge */}
                    <div className="absolute top-6 right-6 text-slate-400 z-30 flex items-center gap-1.5 text-[9px] font-mono tracking-widest bg-[#0B0F12]/90 px-2.5 py-1 rounded-md border border-[#262C31]">
                      <Lock size={11} className="text-[#0E93A3] animate-pulse" />
                      <span>SECURE VIEW</span>
                    </div>

                    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-white select-none">
                      <img
                        src="/images/brand/1left.webp"
                        alt="VEXL Wordmark Signature"
                        className="w-full h-full object-contain pointer-events-none transition-transform duration-500 group-hover:scale-[1.03] rounded-xl p-2"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                      {/* Transparent protection shield overlay */}
                      <div
                        className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-between text-[10px] font-mono text-slate-500 px-1">
                      <span>FRAME 01 // STANDALONE WORDMARK</span>
                      <span>STATUS: PROTECTED_ASSET</span>
                    </div>
                  </div>

                  {/* Right Frame: VEXL Geometry & Clear Space */}
                  <div className="relative overflow-hidden rounded-2xl border border-[#262C31] bg-[#12161A] p-4 group w-full">
                    {/* Lock Badge */}
                    <div className="absolute top-6 right-6 text-slate-400 z-30 flex items-center gap-1.5 text-[9px] font-mono tracking-widest bg-[#0B0F12]/90 px-2.5 py-1 rounded-md border border-[#262C31]">
                      <Lock size={11} className="text-[#0E93A3] animate-pulse" />
                      <span>SECURE VIEW</span>
                    </div>

                    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-white select-none">
                      <img
                        src="/images/brand/1right.webp"
                        alt="VEXL Geometry & Clear Space"
                        className="w-full h-full object-contain pointer-events-none transition-transform duration-500 group-hover:scale-[1.03] rounded-xl p-1"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                      {/* Transparent protection shield overlay */}
                      <div
                        className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-between text-[10px] font-mono text-slate-500 px-1">
                      <span>FRAME 02 // GEOMETRY & CLEAR SPACE</span>
                      <span>STATUS: PROTECTED_ASSET</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 06 Dos and Don'ts: Wordmark */}
              <div ref={sectionRefs.dos} id="dos" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">05 DOS & DON'TS: WORDMARK</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'المسموح والممنوع: الشعار اللفظي' : "Dos and Don'ts: Wordmark"}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'للحفاظ على الهوية البصرية والقوة التعبيرية للشعار اللفظي لـ VEXL، يجب استخدامه دائماً بالتهيئة والشكل المعتمدين. يُمنع منعاً باتاً تعديل أبعاد الشعار، أو تغيير ألوانه، أو قصه، أو استخدامه كقناع، أو إضافة تأثيرات بصرية قد تضعف وضوحه.'
                    : 'To preserve the visual authority and clarity of the VEXL wordmark, it must always be displayed in its approved configuration. Any distortion, unapproved color swap, cropping, masking, or effects application weakens the identity and is strictly prohibited.'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                  {DONTS_ITEMS.map((item) => (
                    <DosDontsCard key={item.id} item={item} isAr={isAr} />
                  ))}
                </div>
              </div>

              {/* 07 Blossom Logo & Spacing */}
              <div ref={sectionRefs.blossomlogo} id="blossomlogo" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">06 BLOSSOM LOGO</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'شعار بلوسوم' : 'Blossom'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'إن شعار Blossom هو أكثر من مجرد رمز مرئي؛ فهو يمثل الفلسفة الأساسية التي توجه نهجنا في التصميم والابتكار. يجسد الشعار في جوهره التقاطع الديناميكي بين الإنسانية والتكنولوجيا - القوتان اللتان تشكلان عالمنا وتلهمان عملنا. يجسد التصميم انسيابية ودفء التفكير المتمحور حول الإنسان من خلال استخدام الدوائر، بينما تقدم الزوايا القائمة الدقة والهيكل الذي تتطلبه التكنولوجيا.'
                    : 'The Blossom logo is more than just a visual symbol; it represents the core philosophy that guides our approach to design and innovation. At its heart, the logo captures the dynamic intersection between humanity and technology—two forces that shape our world and inspire our work. The design embodies the fluidity and warmth of human-centered thinking through the use of circles, while right angles introduce the precision and structure that technology demands.'}
                </p>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                    <ImagePlaceholder
                      labelEn="Blossom spacing construction grid"
                      labelAr="مخطط البناء الهندسي ومسافات بلوسوم"
                      aspectRatio="aspect-square"
                      filename="blossom_logo_grid.webp"
                      isAr={isAr}
                    />
                    <ImagePlaceholder
                      labelEn="Blossom clear space boundary"
                      labelAr="حدود المساحة الخالية لبلوسوم"
                      aspectRatio="aspect-square"
                      filename="blossom_logo_clearspace.webp"
                      isAr={isAr}
                    />
                  </div>

                  <div className="text-center text-xs font-mono text-slate-500">
                    {isAr
                      ? 'استخدم المسافات المحددة للسماح لرمز بلوسوم بالتنفس.'
                      : 'Use the prescribed spacing to let the Blossom breathe.'}
                  </div>
                </div>

                {/* Dos & Don'ts: Blossom */}
                <div className="pt-8 space-y-6">
                  <div className="space-y-2">
                    <h4 className="text-lg md:text-xl font-bold tracking-tight text-white">
                      {isAr ? 'المسموح والممنوع: رمز بلوسوم' : "Dos and Don'ts: Blossom"}
                    </h4>
                    <p className="text-[#9AA5AC] text-xs md:text-sm leading-relaxed font-normal">
                      {isAr
                        ? 'إرشادات تفصيلية للاستخدام الصحيح والخاطئ لرمز بلوسوم لضمان اتساق الهوية البصرية.'
                        : 'Detailed rules on the correct and incorrect usage of the Blossom logo to maintain brand consistency.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                    {BLOSSOM_DOS_DONTS_ITEMS.map((item) => (
                      <BlossomDosDontsCard key={item.id} item={item} isAr={isAr} />
                    ))}
                  </div>
                </div>
              </div>

              {/* 08 Color Swatches & CSS Token Registry */}
              <div ref={sectionRefs.colorsystem} id="colorsystem" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">07 COLOR REGISTRY</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'لوحة الألوان وسجل رموز التصميم' : 'Unified Design Palette'}
                  </h3>
                </div>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed font-medium">
                  {isAr
                    ? 'الألوان الرسمية المعتمدة في واجهات الويب وتطبيق الديسكتوب. انقر فوق زر النسخ للحصول على كود HEX الخاص بكل لون.'
                    : 'The official digital color codes for VEXL platforms. Hex codes can be copied to the clipboard with metadata settings.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {COLOR_TOKENS.map((token, i) => (
                    <div
                      key={i}
                      className="bg-[#12161A] border border-[#262C31] rounded-xl overflow-hidden flex flex-col justify-between"
                    >
                      <div className={`w-full aspect-[2/1] ${token.bgClass} relative flex items-center justify-center`}>
                        <span className="absolute top-2 left-2 text-[8px] font-mono opacity-30 select-none">SWATCH</span>
                      </div>
                      <div className="p-4 space-y-3 bg-[#010609] flex-grow flex flex-col justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-white">{isAr ? token.nameAr : token.nameEn}</h4>
                          <span className="text-[9px] text-slate-500 font-mono block mt-0.5">{isAr ? token.typeAr : token.typeEn}</span>
                        </div>

                        <div className="space-y-1 text-[10px] font-mono text-slate-400">
                          <div>HEX: {token.hex}</div>
                          <div>RGB: {token.rgb}</div>
                          <div>HSL: {token.hsl}</div>
                          <div className="text-[#0E93A3]">{token.variable}</div>
                        </div>

                        <div className="pt-2 border-t border-white/5 flex items-center justify-between">
                          <span className="text-[10px] font-mono text-slate-500">
                            {copiedHex === token.hex ? (isAr ? 'تم النسخ!' : 'Copied!') : (isAr ? 'نسخ كود اللون' : 'Copy HEX')}
                          </span>
                          <button
                            onClick={() => handleCopyHex(token.hex)}
                            className="p-1.5 hover:bg-white/5 rounded text-slate-400 hover:text-white transition-colors"
                          >
                            {copiedHex === token.hex ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 09 Typography Hierarchy */}
              <div ref={sectionRefs.typography} id="typography" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">08 TYPOGRAPHY HIERARCHY</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'قواعد الخطوط والتدرج الهرمي' : 'Type Family Specifications'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'نظام الخطوط مبني على وزن 500 ومظهر مكثف (Condensed) للعناوين الكبيرة، وهي التوقيع البصري الهندسي للبراند. يتم تفعيل "tnum" للأرقام والبيانات الجغرافية والعلمية كدليل على هوية المنصة. خط IBM Plex Sans Condensed للعناوين، وInter للنصوص والواجهات، وJetBrains Mono للمؤشرات والبيانات والأكواد.'
                    : 'The type system is built on weight 500 display tiers using a condensed, structured font — the brand\'s load-bearing design signature. "tnum" is utilized strictly on spatial measurements and scientific indices. IBM Plex Sans Condensed provides display authority, Inter ensures UI legibility, and JetBrains Mono handles scientific metrics and code.'}
                </p>

                {/* Typography Principles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-xl">
                    <div className="text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest mb-2">Display-XL / LG — Display Font</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed">
                      {isAr ? 'يتم استخدام خط IBM Plex Sans Condensed للعناوين الكبيرة بوزن 500 مكثف ليعبر عن طابع هندسي وبنيوي صلب بدلاً من الخطوط النحيفة.' : 'Use IBM Plex Sans Condensed for displays and headers at weight 500 (Medium). This gives the typography a load-bearing, engineering feel rather than a delicate tech look.'}
                    </p>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-xl">
                    <div className="text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest mb-2">tnum — Tabular Figures</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed">
                      {isAr ? 'يتم تفعيل tnum خصيصاً للبيانات المكانية والعلمية (مثل إحداثيات خطوط الطول والعرض، مؤشرات NDVI، قيم R²). لا نستخدمها للعملات أو الأرقام المالية.' : 'Apply tabular figures (tnum) on spatial and scientific indicators only (coordinates, NDVI, R², MAE). Never use it for financial currency since VEXL is a geospatial platform.'}
                    </p>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-xl">
                    <div className="text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest mb-2">Weight 500 — Display Signature</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed">
                      {isAr ? 'العناوين تعرض دائماً بوزن 500 مكثف ومحكم. الخطوط النحيفة أو التحريرية مرفوضة لأنها لا تتناسب مع منصة بنية تحتية هندسية.' : 'Display tiers always render at weight 500 (Medium) condensed. Thin weights are rejected because they diminish the structural look VEXL is built around.'}
                    </p>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-xl">
                    <div className="text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest mb-2">Negative Tracking</div>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed">
                      {isAr ? 'تراكينج سلبي خفيف (-0.5px للعناوين الكبيرة جداً) ليعطي مظهراً هندسياً محكماً ومرصوصاً ومقروءاً على جميع الشاشات.' : 'Tight letter-spacing scales from -0.5px at display-xl down to -0.3px at display-lg to give a precise, compact presentation.'}
                    </p>
                  </div>
                </div>

                {/* Typography Tab System */}
                <div className="w-full">
                  {/* Tab Selector */}
                  <div className="flex pb-2 mb-8 gap-6 overflow-x-auto select-none scrollbar-none">
                    {[
                      { id: 'outfit', name: isAr ? 'خط العناوين (IBM Plex Sans Condensed)' : 'IBM Plex Sans Condensed (Display)' },
                      { id: 'inter', name: isAr ? 'خط النصوص والواجهات (Inter)' : 'Inter (Body / UI)' },
                      { id: 'mono', name: isAr ? 'خط الأكواد والمؤشرات (Mono)' : 'JetBrains Mono (Metrics / Code)' },
                    ].map((tab) => {
                      const isActive = activeFontTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveFontTab(tab.id as any)}
                          className="relative pb-2.5 text-xs font-semibold tracking-wider transition-all duration-300 whitespace-nowrap text-left"
                        >
                          <span className={isActive ? 'text-[#0E93A3]' : 'text-slate-450 hover:text-white'}>
                            {tab.name}
                          </span>
                          {isActive && (
                            <motion.div
                              layoutId="activeFontBorder"
                              className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0E93A3]"
                              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab Contents */}
                  <AnimatePresence mode="wait">
                    {activeFontTab === 'outfit' && (
                      <motion.div
                        key="outfit"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-8 text-left"
                      >
                        {/* Font Info Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-[#12161A] p-6 rounded-2xl border border-[#262C31] relative overflow-hidden">
                          <div>
                            <div className="text-3xl font-medium text-white tracking-tight" style={{ fontFamily: 'IBM Plex Sans Condensed' }}>IBM Plex Sans Condensed</div>
                            <div className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-widest mt-1">Condensed Display Sans-Serif — Primary Display Font</div>
                          </div>
                          <p className="text-[#9AA5AC] text-xs md:text-sm max-w-xl leading-relaxed font-normal">
                            {isAr
                              ? 'يُسْتَخْدَم خَط IBM Plex Sans Condensed للتأكيد الهندسي في العناوين والافتتاحيات الرقمية لـ VEXL بوزن 500 مكثف. يتميز بزواياه الهندسية المتزنة وعرضه المكثف الذي يعكس طابع التصميم الجغرافي البنيوي.'
                              : "IBM Plex Sans Condensed is VEXL's primary display face. Built with structured path ratios and a slightly condensed form, it commands load-bearing authority on dashboard and marketing layout titles at weight 500."}
                          </p>
                        </div>

                        {/* Specimens Matrix */}
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            { name: isAr ? 'شبه عريض (SemiBold)' : 'SemiBold', weight: '600', weightClass: 'font-semibold' },
                            { name: isAr ? '★ متوسط (Medium) — توقيع البراند' : '★ Medium (500) — Brand Signature', weight: '500', weightClass: 'font-medium' },
                            { name: isAr ? 'عادي (Regular)' : 'Regular', weight: '400', weightClass: 'font-normal' }
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-5 bg-[#171C21] border border-[#262C31] rounded-xl hover:border-[#0E93A3]/20 transition-all group">
                              <div className="flex flex-col text-left">
                                <span className="text-xs font-semibold text-[#EDF0F1] group-hover:text-white transition-colors">{item.name}</span>
                                <span className="text-[9px] font-mono text-[#66707A] mt-0.5">Weight: {item.weight}</span>
                              </div>

                              <div className="flex items-center gap-12 md:gap-24">
                                <span className={`text-4xl md:text-5xl text-white select-none ${item.weightClass}`} style={{ fontFamily: 'IBM Plex Sans Condensed' }}>Aa</span>
                                <span className={`text-4xl md:text-5xl text-[#66707A] select-none ${item.weightClass}`} style={{ fontFamily: 'IBM Plex Sans Condensed' }}>Aa</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Full Character Specimen Sheet */}
                        <div className="bg-[#12161A] p-6 rounded-2xl border border-[#262C31] space-y-4">
                          <div className="text-[9px] font-mono text-[#66707A] uppercase tracking-widest">Character Specimen (IBM Plex Sans Condensed 500)</div>
                          <div className="text-white text-lg md:text-2xl font-medium tracking-wide break-all select-none opacity-90" style={{ fontFamily: 'IBM Plex Sans Condensed' }}>
                            ABCDEFGHIJKLMNOPQRSTUVWXYZ
                          </div>
                          <div className="text-[#9AA5AC] text-base md:text-xl font-normal tracking-wide break-all select-none opacity-80" style={{ fontFamily: 'IBM Plex Sans Condensed' }}>
                            abcdefghijklmnopqrstuvwxyz
                          </div>
                          <div className="text-[#0E93A3] text-sm md:text-lg font-mono tracking-widest break-all select-none opacity-90">
                            0123456789 !@#$%^&*()_+=-[]&#123;&#125;|\:;&quot;'&lt;&gt;?,./
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeFontTab === 'inter' && (
                      <motion.div
                        key="inter"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-8 text-left"
                      >
                        {/* Font Info Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-[#12161A] p-6 rounded-2xl border border-[#262C31] relative overflow-hidden">
                          <div>
                            <div className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: 'Inter' }}>Inter</div>
                            <div className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-widest mt-1">Neo-Grotesque Sans-Serif — UI & Body Font</div>
                          </div>
                          <p className="text-slate-350 text-xs md:text-sm max-w-xl leading-relaxed font-medium">
                            {isAr
                              ? 'خط Inter هو خط عالي الوضوح مصمم خصيصاً لشاشات الحواسب. يُستخدم في نصوص المقالات الطويلة، وجداول البيانات، وعناصر التحكم في الواجهات لضمان القراءة السلسة.'
                              : "Inter is VEXL's workhorse typeface, optimized for computer screens and dense data displays. It maintains exceptional readability at micro-scales in control panels and telemetry screens."}
                          </p>
                        </div>

                        {/* Specimens Matrix */}
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            { name: isAr ? 'عريض (Bold)' : 'Bold', weight: '700', weightClass: 'font-bold' },
                            { name: isAr ? 'شبه عريض (SemiBold)' : 'SemiBold', weight: '600', weightClass: 'font-semibold' },
                            { name: isAr ? 'متوسط (Medium)' : 'Medium', weight: '500', weightClass: 'font-medium' },
                            { name: isAr ? 'عادي (Regular)' : 'Regular', weight: '400', weightClass: 'font-normal' }
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-5 bg-[#171C21] border border-[#262C31] rounded-xl hover:border-[#0E93A3]/20 transition-all group">
                              <div className="flex flex-col text-left">
                                <span className="text-xs font-semibold text-[#EDF0F1] group-hover:text-white transition-colors">{item.name}</span>
                                <span className="text-[9px] font-mono text-[#66707A] mt-0.5">Weight: {item.weight}</span>
                              </div>

                              <div className="flex items-center gap-12 md:gap-24">
                                <span className={`text-4xl md:text-5xl text-white select-none ${item.weightClass}`} style={{ fontFamily: 'Inter' }}>Aa</span>
                                <span className={`text-4xl md:text-5xl text-slate-400 select-none italic ${item.weightClass}`} style={{ fontFamily: 'Inter' }}>Aa</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Full Character Specimen Sheet */}
                        <div className="bg-[#12161A] p-6 rounded-2xl border border-[#262C31] space-y-4">
                          <div className="text-[9px] font-mono text-[#66707A] uppercase tracking-widest">Character Specimen (Inter 400/700)</div>
                          <div className="text-white text-lg md:text-2xl font-bold tracking-widest break-all select-none opacity-90" style={{ fontFamily: 'Inter' }}>
                            ABCDEFGHIJKLMNOPQRSTUVWXYZ
                          </div>
                          <div className="text-[#9AA5AC] text-base md:text-xl font-normal tracking-widest break-all select-none opacity-80" style={{ fontFamily: 'Inter' }}>
                            abcdefghijklmnopqrstuvwxyz
                          </div>
                          <div className="text-[#0E93A3] text-sm md:text-lg font-mono tracking-widest break-all select-none opacity-90">
                            0123456789 !@#$%^&*()_+=-[]&#123;&#125;|\:;&quot;'&lt;&gt;?,./
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeFontTab === 'mono' && (
                      <motion.div
                        key="mono"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-8 text-left"
                      >
                        {/* Font Info Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-[#12161A] p-6 rounded-2xl border border-[#262C31] relative overflow-hidden">
                          <div>
                            <div className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: 'JetBrains Mono' }}>JetBrains Mono</div>
                            <div className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-widest mt-1">Monospaced — Developer & Spatial Font</div>
                          </div>
                          <p className="text-slate-350 text-xs md:text-sm max-w-xl leading-relaxed font-medium">
                            {isAr
                              ? 'خط JetBrains Mono مصمم خصيصاً للمطورين لقراءة الأكواد وعرض الإحداثيات والرموز الهندسية بدقة. يتميز بارتفاع الحروف المتوازن والتمييز البصري الفائق للعلامات.'
                              : "JetBrains Mono is designed specifically for programmers and developers. It features highly distinct character forms, balanced weights, and optimized spacing for code blocks."}
                          </p>
                        </div>

                        {/* Specimens Matrix */}
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            { name: isAr ? 'عريض (Bold)' : 'Bold', weight: '700', weightClass: 'font-bold' },
                            { name: isAr ? 'متوسط (Medium)' : 'Medium', weight: '500', weightClass: 'font-medium' },
                            { name: isAr ? 'عادي (Regular)' : 'Regular', weight: '400', weightClass: 'font-normal' },
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-5 bg-[#171C21] border border-[#262C31] rounded-xl hover:border-[#0E93A3]/20 transition-all group">
                              <div className="flex flex-col text-left">
                                <span className="text-xs font-semibold text-[#EDF0F1] group-hover:text-white transition-colors">{item.name}</span>
                                <span className="text-[9px] font-mono text-[#66707A] mt-0.5">Weight: {item.weight}</span>
                              </div>

                              <div className="flex items-center gap-12 md:gap-24">
                                <span className={`text-4xl md:text-5xl text-white select-none ${item.weightClass}`} style={{ fontFamily: 'JetBrains Mono' }}>Aa</span>
                                <span className={`text-4xl md:text-5xl text-[#66707A] select-none ${item.weightClass}`} style={{ fontFamily: 'JetBrains Mono' }}>Aa</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Full Character Specimen Sheet */}
                        <div className="bg-[#12161A] p-6 rounded-2xl border border-[#262C31] space-y-4">
                          <div className="text-[9px] font-mono text-[#66707A] uppercase tracking-widest">Character Specimen (JetBrains Mono 400/700)</div>
                          <div className="text-white text-lg md:text-2xl font-bold tracking-widest break-all select-none opacity-90" style={{ fontFamily: 'JetBrains Mono' }}>
                            ABCDEFGHIJKLMNOPQRSTUVWXYZ
                          </div>
                          <div className="text-[#9AA5AC] text-base md:text-xl font-normal tracking-widest break-all select-none opacity-80" style={{ fontFamily: 'JetBrains Mono' }}>
                            abcdefghijklmnopqrstuvwxyz
                          </div>
                          <div className="text-[#0E93A3] text-sm md:text-lg font-mono tracking-widest break-all select-none opacity-90">
                            {"const client = new VexlClient({ token: 'vexl_token_2026' });"}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Typography Hierarchy Reference Table */}
              <div className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">08.B TYPE HIERARCHY SCALE</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'جدول التدرج الهرمي للخطوط' : 'Typography Hierarchy Reference'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'الجدول الكامل لجميع أحجام وأوزان الخطوط المعتمدة مع تراكينج الحروف وارتفاع السطر لكل مستوى وفقاً لـ VEXL Design System V2.'
                    : 'The complete mapping of every V2 design system typography token — size, weight, line-height, letter-spacing, and use case.'}
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="py-3 pr-4 text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest">Token</th>
                        <th className="py-3 pr-4 text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest">Size</th>
                        <th className="py-3 pr-4 text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest">Weight</th>
                        <th className="py-3 pr-4 text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest hidden md:table-cell">Line H.</th>
                        <th className="py-3 pr-4 text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest hidden md:table-cell">Tracking</th>
                        <th className="py-3 text-[9px] font-mono text-[#0E93A3] uppercase tracking-widest">{isAr ? 'الاستخدام' : 'Use'}</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300 font-mono">
                      {[
                        { token: 'display-xl', size: '52px', weight: '500', lh: '1.05', ls: '-0.5px', use: 'Hero headline (IBM Plex Sans Condensed)' },
                        { token: 'display-lg', size: '36px', weight: '500', lh: '1.1', ls: '-0.3px', use: 'Section opener (IBM Plex Sans Condensed)' },
                        { token: 'heading-md', size: '22px', weight: '500', lh: '1.25', ls: '0', use: 'Card title (IBM Plex Sans Condensed)' },
                        { token: 'body-lg', size: '17px', weight: '400', lh: '1.6', ls: '0', use: 'Marketing body lead (Inter)' },
                        { token: 'body-md', size: '15px', weight: '400', lh: '1.55', ls: '0', use: 'Default body / UI (Inter)' },
                        { token: 'data-mono', size: '14px', weight: '500', lh: '1.4', ls: '-0.2px', use: 'Coordinates, NDVI, R², MAE (JetBrains Mono + tnum)' },
                        { token: 'caption', size: '13px', weight: '400', lh: '1.4', ls: '0', use: 'Helper, data citations (Inter)' },
                        { token: 'eyebrow', size: '11px', weight: '500', lh: '1.2', ls: '1.2px', use: 'All-caps section labels (JetBrains Mono)' }
                      ].map((row, idx) => (
                        <tr key={idx} className="border-b border-white/[0.04] hover:bg-slate-950/40 transition-colors">
                          <td className="py-2.5 pr-4 text-white font-semibold text-[10px]">{row.token}</td>
                          <td className="py-2.5 pr-4 text-[10px]">{row.size}</td>
                          <td className="py-2.5 pr-4 text-[10px]">{row.weight}</td>
                          <td className="py-2.5 pr-4 text-[10px] hidden md:table-cell">{row.lh}</td>
                          <td className="py-2.5 pr-4 text-[10px] hidden md:table-cell">{row.ls}</td>
                          <td className="py-2.5 text-[10px] text-slate-400">{row.use}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 10 Border Radius Geometry System */}
              <div ref={sectionRefs.radius} id="radius" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">09 BORDER RADIUS SCALE</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'نظام انحناء أركان وحواف العناصر' : 'Border Radius Specifications'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'يتم استخدام انحناء حواف طفيف وحاد للمنتجات السحابية الكلاسيكية لفكسل لتأكيد المظهر الهندسي الدقيق.'
                    : 'The radius metrics define the design standard for the cloud core, utilizing sharp, crisp borders for visual precision.'}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-[4px] text-center">
                    <div className="w-full aspect-square bg-[#0E93A3]/10 rounded-[4px] mb-2 border border-[#0E93A3]/30" />
                    <div className="text-xs font-bold text-white">rounded.xs</div>
                    <span className="text-[9px] font-mono text-[#66707A] block mt-1">4px (Tags/Hairlines)</span>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-[6px] text-center">
                    <div className="w-full aspect-square bg-[#0E93A3]/10 rounded-[6px] mb-2 border border-[#0E93A3]/30" />
                    <div className="text-xs font-bold text-white">rounded.sm</div>
                    <span className="text-[9px] font-mono text-[#66707A] block mt-1">6px (Inputs/Fields)</span>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-[8px] text-center">
                    <div className="w-full aspect-square bg-[#0E93A3]/10 rounded-[8px] mb-2 border border-[#0E93A3]/30" />
                    <div className="text-xs font-bold text-white">rounded.md</div>
                    <span className="text-[9px] font-mono text-[#66707A] block mt-1">8px (Compact Cards/Alerts)</span>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-[12px] text-center">
                    <div className="w-full aspect-square bg-[#0E93A3]/10 rounded-[12px] mb-2 border border-[#0E93A3]/30" />
                    <div className="text-xs font-bold text-white">rounded.lg</div>
                    <span className="text-[9px] font-mono text-[#66707A] block mt-1">12px (Feature Cards)</span>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-[16px] text-center">
                    <div className="w-full aspect-square bg-[#0E93A3]/10 rounded-[16px] mb-2 border border-[#0E93A3]/30" />
                    <div className="text-xs font-bold text-white">rounded.xl</div>
                    <span className="text-[9px] font-mono text-[#66707A] block mt-1">16px (Mockups/Apps)</span>
                  </div>
                  <div className="p-4 bg-[#12161A] border border-[#262C31] rounded-full text-center">
                    <div className="w-full aspect-square bg-[#0E93A3]/10 rounded-full mb-2 border border-[#0E93A3]/30" />
                    <div className="text-xs font-bold text-white">rounded.pill</div>
                    <span className="text-[9px] font-mono text-[#66707A] block mt-1">9999px (Buttons/Pills)</span>
                  </div>
                </div>
              </div>

              {/* 11 Product Naming Registry */}
              <div ref={sectionRefs.naming} id="naming" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">10 PRODUCT NAMING REGISTRY</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'سجل الأسماء والعلامات المعتمدة للمنتجات' : 'Approved Product Nomenclature'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'يجب الالتزام بأسلوب كتابة اسم الشركة في الوثائق الرسمية والتسويقية وفي جميع أكواد البرمجة.'
                    : 'The VEXL naming conventions are absolute. Keep names matching the standard nomenclature definitions.'}
                </p>

                <div className="w-full overflow-hidden border border-[#262C31] rounded-xl bg-[#12161A]">
                  <table className="w-full border-collapse text-xs md:text-sm text-left">
                    <thead>
                      <tr className="border-b border-white/5 bg-slate-950/60 font-mono text-slate-500">
                        <th className="p-4 font-bold">{isAr ? 'الكيان' : 'Entity'}</th>
                        <th className="p-4 font-bold text-emerald-400">{isAr ? 'الصيغة المعتمدة' : 'Approved Form'}</th>
                        <th className="p-4 font-bold text-red-400">{isAr ? 'الصيغة الممنوعة' : 'Forbidden Form'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-slate-300">
                      <tr>
                        <td className="p-4 font-bold text-white">VEXL Core Brand</td>
                        <td className="p-4 font-mono text-emerald-400">VEXL</td>
                        <td className="p-4 font-mono text-slate-600">vexl, Vexel, vexL</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 12 Partner Lockup Separation Rules */}
              <div ref={sectionRefs.lockups} id="lockups" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">11 PARTNER LOCKUPS</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'قواعد دمج شعارات الشركاء والمطورين' : 'Co-branding Lockup Hierarchy'}
                  </h3>
                </div>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed font-medium">
                  {isAr
                    ? 'يتم دمج شعارات الشركاء والعملاء مع شعار فكسل باستخدام خط فاصل عمودي شفاف لا تقل شفافيته عن 20٪ وبأبعاد متساوية بوازع التوازن البصري.'
                    : 'To represent lock-ups with enterprise partners, separate logos with a 1px vertical line with opacity matching the slate colors.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  {/* Left Frame: Approved Lockup */}
                  <div className="relative overflow-hidden rounded-2xl border border-[#262C31] bg-[#12161A] p-4 group w-full">
                    {/* Lock Badge */}
                    <div className="absolute top-6 right-6 text-slate-400 z-30 flex items-center gap-1.5 text-[9px] font-mono tracking-widest bg-[#0B0F12]/90 px-2.5 py-1 rounded-md border border-[#262C31]">
                      <Lock size={11} className="text-[#0E93A3] animate-pulse" />
                      <span>APPROVED LOCKUP</span>
                    </div>

                    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-white select-none">
                      <img
                        src="/images/brand/part2.webp"
                        alt="Approved Partner Lockup"
                        className="w-full h-full object-contain pointer-events-none transition-transform duration-500 group-hover:scale-[1.03] rounded-xl p-4"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                      {/* Transparent protection overlay */}
                      <div
                        className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-between text-[10px] font-mono text-slate-500 px-1">
                      <span>{isAr ? 'النموذج المعتمد الفعلي' : 'FRAME 01 // APPROVED SYSTEM LOCKUP'}</span>
                      <span>STATUS: MASTER_ASSET</span>
                    </div>
                  </div>

                  {/* Right Frame: Lockup Sizing Blueprint */}
                  <div className="relative overflow-hidden rounded-2xl border border-[#262C31] bg-[#12161A] p-4 group w-full">
                    {/* Lock Badge */}
                    <div className="absolute top-6 right-6 text-slate-400 z-30 flex items-center gap-1.5 text-[9px] font-mono tracking-widest bg-[#0B0F12]/90 px-2.5 py-1 rounded-md border border-[#262C31]">
                      <Lock size={11} className="text-[#0E93A3] animate-pulse" />
                      <span>SPECIFICATIONS</span>
                    </div>

                    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-white select-none">
                      <img
                        src="/images/brand/part1.webp"
                        alt="Partner Lockup Specifications"
                        className="w-full h-full object-contain pointer-events-none transition-transform duration-500 group-hover:scale-[1.03] rounded-xl p-4"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                      {/* Transparent protection overlay */}
                      <div
                        className="absolute inset-0 z-20 bg-transparent select-none cursor-default rounded-xl"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-between text-[10px] font-mono text-slate-500 px-1">
                      <span>{isAr ? 'مخطط الأبعاد والمقاسات' : 'FRAME 02 // SIZING & CLEAR SPACE SPECS'}</span>
                      <span>STATUS: METRIC_BLUEPRINT</span>
                    </div>
                  </div>
                </div>

                {/* Dos & Don'ts: Brand Partnerships */}
                <div className="pt-8 space-y-6">
                  <div className="space-y-2">
                    <h4 className="text-lg md:text-xl font-bold tracking-tight text-white">
                      {isAr ? 'المسموح والممنوع: دمج شعارات الشركاء' : "Dos and Don'ts: Brand Partnerships"}
                    </h4>
                    <p className="text-slate-400 text-xs md:text-sm leading-relaxed font-medium">
                      {isAr
                        ? 'إرشادات تفصيلية للاستخدام الصحيح والخاطئ عند دمج شعار VEXL مع شعارات الشركاء لضمان الاتساق والوضوح.'
                        : 'Detailed rules on the correct and incorrect usage of partner lockups to ensure visual consistency and clarity.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                    {PARTNER_DOS_DONTS_ITEMS.map((item) => (
                      <PartnerDosDontsCard key={item.id} item={item} isAr={isAr} />
                    ))}
                  </div>
                </div>
              </div>



              {/* 13 Assets Center */}
              <div ref={sectionRefs.assets} id="assets" className="scroll-mt-28 space-y-8 pb-16">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-[#0E93A3] tracking-widest uppercase">12 ASSETS CENTER</span>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    {isAr ? 'طلب حقيبة الأصول الرقمية' : 'Brand Kit Request Center'}
                  </h3>
                </div>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {isAr
                    ? 'سجل بريدك الإلكتروني أدناه للانضمام إلى قائمة المستلمين والحصول على حقيبة أصول هوية فكسل الكاملة فور صدورها.'
                    : 'Join the wishlist to receive the complete VEXL brand identity assets package directly in your inbox.'}
                </p>

                <div className="bg-[#12161A] border border-[#262C31] rounded-2xl p-8 md:p-12 relative overflow-hidden">

                  {!emailSubmitted ? (
                    <form onSubmit={handleEmailSubmit} className="relative z-10 max-w-xl space-y-6">
                      <div className="space-y-2">
                        <h4 className="text-xl font-bold text-white">
                          {isAr ? 'احصل على الحقيبة الرقمية الكاملة' : 'Get the Complete Brand Kit'}
                        </h4>
                        <p className="text-slate-400 text-sm leading-relaxed">
                          {isAr
                            ? 'أدخل بريدك الإلكتروني لتلقي حقيبة التصميم الكاملة (تتضمن أصول المتجهات عالية الجودة، وأكواد الرموز، وأدلة الألوان) مباشرة فور صدورها.'
                            : 'Submit your email to receive the complete design kit (including high-resolution vector assets, icon codes, and color guidelines) directly in your inbox.'}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-500">
                            <Mail size={16} />
                          </div>
                          <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={isAr ? 'البريد الإلكتروني' : 'Enter your email address'}
                          className="w-full bg-[#171C21] border border-[#262C31] rounded-md py-3 pl-10 pr-4 text-sm text-white placeholder-[#66707A] focus:outline-none focus:ring-2 focus:ring-[#0E93A3] focus:ring-offset-2 transition-all font-normal"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-sm py-3 px-6 rounded-md transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                          {isSubmitting ? (
                            <RefreshCw className="animate-spin" size={16} />
                          ) : (
                            <>
                              <span>{isAr ? 'انضم للقائمة' : 'Join Wishlist'}</span>
                              <ArrowRight size={16} className={`transition-transform duration-300 ${isAr ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative z-10 text-center py-6 space-y-4 max-w-md mx-auto"
                    >
                      <div className="w-16 h-16 bg-[#0E93A3]/10 border border-[#0E93A3]/20 rounded-full flex items-center justify-center mx-auto text-[#0E93A3]">
                        <CheckCircle2 size={32} />
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-lg font-bold text-white">
                          {isAr ? 'تم تسجيل طلبك بنجاح!' : 'Submission Successful!'}
                        </h4>
                        <p className="text-[#9AA5AC] text-sm leading-relaxed">
                          {isAr
                            ? `تم تسجيل البريد الإلكتروني (${email}) بنجاح في قائمة الانتظار. سنرسل لك ملفات الهوية قريباً جداً.`
                            : `Email (${email}) has been successfully added to the waitlist. We will deliver the brand assets to you very soon.`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEmailSubmitted(false);
                          setEmail('');
                        }}
                        className="text-xs text-[#0E93A3] hover:underline font-mono"
                      >
                        {isAr ? 'تسجيل بريد إلكتروني آخر' : 'Submit another email'}
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>

            </main>
          </div>

          {/* Legal & Usage Footer */}
          <div className="mt-20 pt-16 space-y-16 px-4 md:px-0 max-w-5xl mx-auto">

            {/* Contact */}
            <div className="space-y-5">
              <h4 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
                <Mail size={18} className="text-[#0E93A3]" />
                {isAr ? 'التواصل' : 'Contact'}
              </h4>
              <div className="space-y-3 text-sm text-[#9AA5AC] leading-relaxed font-normal">
                <p>
                  {isAr
                    ? 'للاستفسارات القانونية والتجارية، يرجى التواصل عبر '
                    : 'For legal and licensing inquiries, please contact '}
                  <a href="mailto:vexl.gis@gmail.com" className="text-[#0E93A3] hover:underline font-mono text-xs font-semibold">vexl.gis@gmail.com</a>
                  {'.'}
                </p>
                <p>
                  {isAr
                    ? 'لطلبات استخدام الشعارات والأصول البصرية، أو أي استفسارات تتعلق بإرشادات الهوية، يرجى التواصل عبر '
                    : 'For permission requests to use our logos, questions about these guidelines, or partnership communications, please contact '}
                  <a href="mailto:vexl.gis@gmail.com" className="text-[#0E93A3] hover:underline font-mono text-xs font-semibold">vexl.gis@gmail.com</a>
                  {'.'}
                </p>
              </div>
            </div>

            {/* Press Releases */}
            <div className="space-y-5">
              <h4 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
                <FileText size={18} className="text-[#0E93A3]" />
                {isAr ? 'البيانات الصحفية' : 'Press Releases'}
              </h4>
              <div className="space-y-3 text-sm text-[#9AA5AC] leading-relaxed font-normal">
                <p>
                  {isAr
                    ? 'عند الإشارة إلى شركتنا في المواد الصحفية، يرجى تعريف VEXL كشركة متخصصة في الذكاء الجغرافي المكاني والبنية التحتية الجيومكانية المدعومة بالذكاء الاصطناعي. مهمة VEXL هي تمكين المؤسسات والحكومات من اتخاذ قرارات مكانية ذكية وفعالة.'
                    : "When mentioning our company in press materials, please identify VEXL as a Spatial Intelligence and GeoAI infrastructure company. VEXL's mission is to empower organizations and governments to make smarter, data-driven spatial decisions."}
                </p>
                <p>
                  {isAr
                    ? 'إذا تجاوزت الإعلانات أو المنشورات الحالات الموضحة أعلاه، أو كان لديك استفسار حول إرشاداتنا، تواصل مع فريق العلاقات الإعلامية على '
                    : "If announcements, blogs, or other communications go beyond the cases outlined above, or if you have questions about our guidelines, reach out to VEXL's press team at "}
                  <a href="mailto:vexl.gis@gmail.com" className="text-[#0E93A3] hover:underline font-mono text-xs font-semibold">vexl.gis@gmail.com</a>
                  {'.'}
                </p>
              </div>
            </div>

            {/* Usage Terms */}
            <div className="space-y-5">
              <h4 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
                <Shield size={18} className="text-[#0E93A3]" />
                {isAr ? 'شروط الاستخدام' : 'Usage Terms'}
              </h4>
              <div className="space-y-3 text-sm text-[#9AA5AC] leading-relaxed font-normal">
                <p>
                  {isAr
                    ? 'يشمل مصطلح "العلامات" كل ما نستخدمه لتعريف منتجاتنا وخدماتنا، بما في ذلك الأسماء والشعارات والأيقونات وعناصر التصميم. باستخدامك لعلامات VEXL، فأنت توافق على أننا نمتلكها وأن أي قيمة حسنة ناتجة عن استخدامك تعود لصالحنا.'
                    : "The term \"Marks\" includes anything we use to identify our goods or services, including our names, logos, icons, and design elements. By using VEXL's Marks, you agree that we own them and that any goodwill generated by your use benefits us."}
                </p>
                <p className="font-medium text-slate-300">
                  {isAr ? 'الإذن باستخدام علاماتنا محدود بالضوابط التالية:' : 'Permission to use our Marks is limited in the following ways:'}
                </p>
                <ul className="space-y-2 list-none">
                  {(isAr ? [
                    'استخدم علاماتنا فقط وفقاً لهذه الإرشادات.',
                    'الإذن الممنوح غير حصري (يمكننا منحه للآخرين) وغير قابل للتحويل (لا يمكنك منحه للآخرين).',
                    'لا تُبرز علاماتنا بشكل أكثر وضوحاً من اسم شركتك أو علاماتها.',
                    'قد نقوم بتحديث هذا الدليل، ويجب إجراء التغييرات وفقاً لتحديثاتنا خلال فترة زمنية معقولة.',
                    'يمكننا مراجعة استخدام علاماتنا وطلب تعديلات إذا لزم الأمر.',
                    'يمكننا إنهاء الإذن باستخدام علاماتنا في أي وقت، ويجب التوقف عن الاستخدام فوراً.',
                    'لا نسمح باستخدام أسماء منتجاتنا (مثل Nova أو CropLens أو GeoAgent) في عناوين التطبيقات أو أسماء الشركات لتجنب الالتباس لدى المستخدمين.'
                  ] : [
                    'Only use our Marks if they adhere to these brand guidelines.',
                    'The permission we grant is non-exclusive (we can give it to others) and non-transferable (you cannot give it to others).',
                    "Do not feature our Marks more prominently than your own company's name or marks.",
                    'We may update the guide, and changes must be made in accordance with our updates within a reasonable time.',
                    'We can review the use of our Marks and require changes if needed.',
                    'We may terminate permission to use our Marks at any time, and usage must stop promptly.',
                    'We do not permit product names (e.g., Nova, CropLens, GeoAgent) in app titles or company names to avoid end-user confusion.'
                  ]).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-[#0E93A3] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  {isAr
                    ? 'يرجى التأكد من أن تطبيقك يتبع جميع سياسات الاستخدام الخاصة بـ VEXL. إذا كان مشروعك يستخدم واجهات برمجة التطبيقات أو النماذج الخاصة بنا، يمكنك الإشارة إلى منتج محدد ولكن كن دقيقاً ومحدداً.'
                    : 'Please ensure your app follows all VEXL usage policies. If your project leverages our APIs or models, you may choose to reference a specific product but be precise and accurate.'}
                </p>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* PREMIUM FOOTER — VEXL Design System v2.0                         */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            <div className="pt-16 pb-8 space-y-14">

              {/* Footer columns */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-10 md:gap-8">

                {/* Column 1: Products */}
                <div className="space-y-4">
                  <h5 className="text-[11px] font-mono text-[#0E93A3] tracking-widest uppercase font-bold">
                    {isAr ? 'المنتجات' : 'Products'}
                  </h5>
                  <ul className="space-y-2.5">
                    {[
                      { en: 'VEXL Nova', ar: 'فكسل نوفا', href: '/products/nova' },
                      { en: 'VEXL GeoAgent', ar: 'العميل الجغرافي', href: '/products/geo' },
                      { en: 'CropLens', ar: 'كروب لينس', href: '/products/croplens' },
                      { en: 'VEXL Workspace', ar: 'مساحة عمل فكسل', href: '/nova-workspace' },
                    ].map((link) => (
                      <li key={link.en}>
                        <a href={link.href} className="text-[13px] text-slate-500 hover:text-white transition-colors duration-200">
                          {isAr ? link.ar : link.en}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 2: Platform */}
                <div className="space-y-4">
                  <h5 className="text-[11px] font-mono text-[#0E93A3] tracking-widest uppercase font-bold">
                    {isAr ? 'المنصة' : 'Platform'}
                  </h5>
                  <ul className="space-y-2.5">
                    {[
                      { en: 'API Documentation', ar: 'التوثيق البرمجي', href: '/portal' },
                      { en: 'Developer Portal', ar: 'بوابة المطورين', href: '/docs' },
                      { en: 'Solutions', ar: 'الحلول', href: '/solutions' },
                      { en: 'Pricing', ar: 'الأسعار', href: '/pricing' },
                      { en: 'System Status', ar: 'حالة النظام', href: 'https://status.vexl-gis.tech', external: true },
                    ].map((link) => (
                      <li key={link.en}>
                        <a
                          href={link.href}
                          {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          className="text-[13px] text-slate-500 hover:text-white transition-colors duration-200 inline-flex items-center gap-1"
                        >
                          {isAr ? link.ar : link.en}
                          {link.external && <ExternalLink size={10} className="text-slate-600" />}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 3: Company */}
                <div className="space-y-4">
                  <h5 className="text-[11px] font-mono text-[#0E93A3] tracking-widest uppercase font-bold">
                    {isAr ? 'الشركة' : 'Company'}
                  </h5>
                  <ul className="space-y-2.5">
                    {[
                      { en: 'Vision', ar: 'الرؤية', href: '/vision' },
                      { en: 'Safety & Trust', ar: 'الأمان والثقة', href: '/safety' },
                      { en: 'Careers', ar: 'الوظائف', href: '/careers' },
                      { en: 'Brand Guidelines', ar: 'إرشادات الهوية', href: '/brand' },
                      { en: 'Contact', ar: 'تواصل معنا', href: '/contact' },
                    ].map((link) => (
                      <li key={link.en}>
                        <a href={link.href} className="text-[13px] text-slate-500 hover:text-white transition-colors duration-200">
                          {isAr ? link.ar : link.en}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 4: Resources */}
                <div className="space-y-4">
                  <h5 className="text-[11px] font-mono text-[#0E93A3] tracking-widest uppercase font-bold">
                    {isAr ? 'الموارد' : 'Resources'}
                  </h5>
                  <ul className="space-y-2.5">
                    {[
                      { en: 'Help Center', ar: 'مركز المساعدة', href: '/help' },
                      { en: 'Release Notes', ar: 'سجل الإصدارات', href: '/changelog' },
                    ].map((link) => (
                      <li key={link.en}>
                        <a href={link.href} className="text-[13px] text-slate-500 hover:text-white transition-colors duration-200">
                          {isAr ? link.ar : link.en}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 5: Terms & Policies */}
                <div className="space-y-4">
                  <h5 className="text-[11px] font-mono text-[#0E93A3] tracking-widest uppercase font-bold">
                    {isAr ? 'الشروط والسياسات' : 'Terms & Policies'}
                  </h5>
                  <ul className="space-y-2.5">
                    {[
                      { en: 'Terms of Use', ar: 'شروط الاستخدام', href: '/terms' },
                      { en: 'Privacy Policy', ar: 'سياسة الخصوصية', href: '/privacy' },
                      { en: 'Other Policies', ar: 'سياسات أخرى', href: '/policies' },
                    ].map((link) => (
                      <li key={link.en}>
                        <a href={link.href} className="text-[13px] text-slate-500 hover:text-white transition-colors duration-200">
                          {isAr ? link.ar : link.en}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bottom bar: Socials — Copyright — Version badge */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">

                {/* Social icons */}
                <div className="flex items-center gap-4">
                  {[
                    {
                      label: 'X / Twitter', href: 'https://x.com/vexl_tech', icon: (
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                      )
                    },
                    {
                      label: 'LinkedIn', href: 'https://linkedin.com/company/vexl', icon: (
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                      )
                    },
                    {
                      label: 'GitHub', href: 'https://github.com/vexl-gis', icon: (
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>
                      )
                    },
                    {
                      label: 'Discord', href: 'https://discord.gg/vexl', icon: (
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561 19.9312 19.9312 0 005.9932 3.0397.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286 19.8975 19.8975 0 006.0023-3.0397.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" /></svg>
                      )
                    },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="text-slate-600 hover:text-white transition-colors duration-200"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>

                {/* Copyright + Manage Cookies */}
                <div className="flex items-center gap-3 text-[11px] font-mono text-slate-600">
                  <span>VEXL © 2024–{new Date().getFullYear()}</span>
                  <span className="text-white/10">|</span>
                  <button
                    type="button"
                    className="hover:text-slate-400 transition-colors"
                  >
                    {isAr ? 'إدارة ملفات تعريف الارتباط' : 'Manage Cookies'}
                  </button>
                </div>

                {/* Version / Design System badge */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 bg-[#12161A] border border-[#262C31] rounded-full px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-mono text-[#66707A] tracking-wider">
                      VEXL Design System v2.0
                    </span>
                  </div>
                </div>
              </div>

              {/* Build metadata strip */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[9px] font-mono text-slate-700/60 tracking-widest uppercase select-none">
                <span>BUILD 2026.05.23</span>
                <span className="text-white/5">·</span>
                <span>DOCS v1.21.0</span>
                <span className="text-white/5">·</span>
                <span>RELEASE TAG: STABLE</span>
                <span className="text-white/5">·</span>
                <span>BRAND SPEC: FINAL</span>
              </div>
            </div>
          </div>

        </div>

      </div>
    </PageTransition>
  );
};
